// Copyright (c) 2026 Joshua S. Plaza. All rights reserved.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
} from '@mui/material';
import {
  Login as LoginIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  CalendarMonth as CalendarMonthIcon,
  MusicNote as MusicNoteIcon,
  MenuBook as MenuBookIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Public as PublicIcon,
} from '@mui/icons-material';


const PublicNavbar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [schoolYrMenuAnchor, setSchoolYrMenuAnchor] = useState(null);
  const [mobileSchoolYrOpen, setMobileSchoolYrOpen] = useState(false);
  const [hymnDialogOpen, setHymnDialogOpen] = useState(false);
  const schoolYrSubItems = [
    { label: 'C.Y 2028', path: '/cy-2028' },
    { label: 'C.Y 2027', path: '/cy-2027' },
    { label: 'C.Y 2026', path: '/cy-2026' },
  ];

  const navItems = [
    { label: 'Home', icon: <HomeIcon />, path: '/' },
    { label: 'About Us', icon: <InfoIcon />, path: '/about-us' },
    { label: 'Enrollment', icon: <PeopleIcon />, path: '/enrollment' },
    { label: 'RNHS Hymn', icon: <MusicNoteIcon />, action: 'openHymn' },
    { label: 'School Yr', icon: <CalendarMonthIcon />, hasSubmenu: true },
    { label: 'User Guide', icon: <MenuBookIcon />, path: '/user-guide' },
  ];

  const districtItem = {
    label: 'District6',
    icon: <PublicIcon />,
    external: 'https://sites.google.com/view/district6/home',
  };

  const canViewDistrict =
    isAuthenticated && (user?.role === 'TEACHER' || user?.role === 'ADMIN');

  const visibleNavItems = canViewDistrict ? [...navItems, districtItem] : navItems;

  const handleSchoolYrMenuOpen = (event) => {
    setSchoolYrMenuAnchor(event.currentTarget);
  };

  const handleSchoolYrMenuClose = () => {
    setSchoolYrMenuAnchor(null);
  };

  const handleSchoolYrSubItemClick = (path) => {
    handleSchoolYrMenuClose();
    setMobileMenuOpen(false);
    navigate(path);
  };

  const handleNavClick = (item) => {
    setMobileMenuOpen(false);
    if (item.action === 'openHymn') {
      setHymnDialogOpen(true);
    } else if (item.external) {
      window.open(item.external, '_blank', 'noopener,noreferrer');
    } else if (item.path) {
      navigate(item.path);
      window.scrollTo(0, 0);
    }
  };

  const handleLogoClick = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  const handleLoginClick = () => {
    if (isAuthenticated) {
      switch (user?.role) {
        case 'ADMIN':
          navigate('/admin');
          break;
        case 'TEACHER':
          navigate('/teacher');
          break;
        case 'STUDENT':
          navigate('/student');
          break;
        case 'PARENT':
          navigate('/parent');
          break;
        default:
          navigate('/login');
      }
    } else {
      navigate('/login');
    }
  };

  return (
    <>
      {/* Navigation Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        color="inherit"
        sx={{
          bgcolor: '#FFFFFF',
          color: '#1A365D',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 64, sm: 80 }, px: { xs: 0 } }}>
            {/* Logo */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
              onClick={handleLogoClick}
            >
              <Box
                component="img"
                src="/rnhs-logo.png"
                alt="RNHS Logo"
                sx={{ height: 48, width: 48, objectFit: 'contain' }}
              />
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700, lineHeight: 1.2 }}>
                  RNHS
                </Typography>
              </Box>
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              {visibleNavItems.map((item) => (
                item.hasSubmenu ? (
                  <Box key={item.label}>
                    <Button
                      onClick={handleSchoolYrMenuOpen}
                      endIcon={<ArrowDropDownIcon />}
                      sx={{
                        color: '#1A365D',
                        fontWeight: 600,
                        px: 2,
                        '&:hover': {
                          color: '#1A365D',
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                    <Menu
                      anchorEl={schoolYrMenuAnchor}
                      open={Boolean(schoolYrMenuAnchor)}
                      onClose={handleSchoolYrMenuClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                      {schoolYrSubItems.map((subItem) => (
                        <MenuItem
                          key={subItem.label}
                          onClick={() => handleSchoolYrSubItemClick(subItem.path)}
                        >
                          {subItem.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                ) : (
                  <Button
                    key={item.label}
                    onClick={() => handleNavClick(item)}
                    sx={{
                      color: '#1A365D',
                      fontWeight: 600,
                      px: 2,
                      '&:hover': {
                        color: '#1A365D',
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                )
              ))}
              <Button
                variant="contained"
                color="primary"
                startIcon={<LoginIcon />}
                onClick={handleLoginClick}
                sx={{ ml: 2 }}
              >
                {isAuthenticated ? 'Go to Dashboard' : 'Login'}
              </Button>
            </Box>

            {/* Mobile Menu Button */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<LoginIcon />}
                onClick={handleLoginClick}
              >
                {isAuthenticated ? 'Dashboard' : 'Login'}
              </Button>
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{ color: '#1A365D' }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 280 },
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
            Menu
          </Typography>
          <IconButton onClick={() => setMobileMenuOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List sx={{ pt: 2 }}>
          {visibleNavItems.map((item) => (
            item.hasSubmenu ? (
              <Box key={item.label}>
                <ListItemButton
                  onClick={() => setMobileSchoolYrOpen(!mobileSchoolYrOpen)}
                  sx={{
                    py: 1.5,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: '#1A365D', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  {mobileSchoolYrOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>
                <Collapse in={mobileSchoolYrOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {schoolYrSubItems.map((subItem) => (
                      <ListItemButton
                        key={subItem.label}
                        sx={{ pl: 6, py: 1 }}
                        onClick={() => handleSchoolYrSubItemClick(subItem.path)}
                      >
                        <ListItemText
                          primary={subItem.label}
                          primaryTypographyProps={{ fontWeight: 400, fontSize: '0.9rem' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </Box>
            ) : (
              <ListItemButton
                key={item.label}
                onClick={() => handleNavClick(item)}
                sx={{
                  py: 1.5,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon sx={{ color: '#1A365D', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItemButton>
            )
          ))}
        </List>
      </Drawer>

      {/* RNHS Hymn Dialog */}
      <Dialog
        open={hymnDialogOpen}
        onClose={() => setHymnDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <MusicNoteIcon />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
              RIZAL NHS HYMN
            </Typography>
          </Box>
          <IconButton
            onClick={() => setHymnDialogOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
              Composer: <strong>Leonevee Silvosa Botero</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Instrumentalist: <strong>Armiel Santillana Cabrera</strong>
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 2, mb: 3 }}>
              Rizal NHS we'll uphold your name<br />
              We devote to you our hearts, our dreams<br />
              Your star forever shines<br />
              Leading us a future bright.
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 2, mb: 3, fontStyle: 'italic' }}>
              Aim high, rise high<br />
              Proud seeds of heroes born<br />
              Alma Mater our dear<br />
              We swear to thee pure loyalty.
            </Typography>
            <Typography variant="body1" sx={{ color: 'primary.main', lineHeight: 2, mb: 3, fontWeight: 600 }}>
              RNHS (perennial claim)<br />
              RNHS (high esteem)<br />
              RNHS, you're great and grand.
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 2, mb: 3 }}>
              Our mentors, brilliance shared<br />
              Their words and love our stronger shield
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              (Repeat Chorus)
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 2, mb: 3 }}>
              As we journey through our fields<br />
              Triumph and glory 'til the end.
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.primary', lineHeight: 2, fontWeight: 500 }}>
              As we journey through our fields<br />
              Triumph and glory 'til the end.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PublicNavbar;
